/**
 * --------------------------------------------------------------------------
 * Removes specified HTML tags from a given string.
 *
 * @param string $input The input string to be stripped of HTML tags.
 * @param array $tags An array of HTML tags to be removed from the input string.
 * @return string The input string with specified HTML tags removed.
 * --------------------------------------------------------------------------
 **/
export function removeTags(input: string, tags: string[]): string {
  const tagsPattern = tags.map((tag) => `</?${tag}[^>]*>`).join('|')
  const regex = new RegExp(tagsPattern, 'gi')
  return input.replace(regex, '')
}

/**
 * --------------------------------------------------------------------------
 * Strips all HTML tags from a given string.
 *
 * @param string $input The input string to be stripped of HTML tags.
 * @return string The input string with all HTML tags removed.
 * --------------------------------------------------------------------------
 **/
export function stripAllTags(input: string): string {
  return input.replace(/<\/?[^>]+(>|$)/g, '')
}

/**
 * --------------------------------------------------------------------------
 * Converts a string to a slug.
 *
 * @param string $str The input string to be converted to a slug.
 * @return string The input string converted to a slug.
 * --------------------------------------------------------------------------
 **/
export function slugify(str: string): string {
  if (!str) return ''

  str = str.replace(/^\s+|\s+$/g, '') // trim leading/trailing white space
  str = str.toLowerCase() // convert string to lowercase
  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-') // remove consecutive hyphens
  return str
}

/**
 * --------------------------------------------------------------------------
 * Trims a specified character from the beginning and end of a string.
 *
 * @param string $str The input string to be trimmed.
 * @param string $ch The character to be trimmed from the input string.
 * @return string The input string with the specified character trimmed from the beginning and end.
 * --------------------------------------------------------------------------
 **/

// TODO: the below trim functions already exist in JS?
export function trim(str, ch) {
  var start = 0,
    end = str.length

  while (start < end && str[start] === ch) ++start

  while (end > start && str[end - 1] === ch) --end

  return start > 0 || end < str.length ? str.substring(start, end) : str
}

/**
 * --------------------------------------------------------------------------
 * Trims a specified character from the beginning of a string.
 *
 * @param string $str The input string to be trimmed.
 * @param string $ch The character to be trimmed from the input string.
 * @return string The input string with the specified character trimmed from the beginning.
 * --------------------------------------------------------------------------
 **/
export function ltrim(str, ch) {
  var start = 0

  while (start < str.length && str[start] === ch) ++start

  return start > 0 ? str.substring(start) : str
}

/**
 * --------------------------------------------------------------------------
 * Trims a specified character from the end of a string.
 *
 * @param string $str The input string to be trimmed.
 * @param string $ch The character to be trimmed from the input string.
 * @return string The input string with the specified character trimmed from the end.
 * --------------------------------------------------------------------------
 **/
export function rtrim(str, ch) {
  var end = str.length

  while (end > 0 && str[end - 1] === ch) --end

  return end < str.length ? str.substring(0, end) : str
}

// TODO: swap out parse() library?
export function decodeSafeEntities(input: string): string {
  if (!input) return ''

  const entities = {
    '&amp;': '&',
    '#38;': '&',
    '&#038;': '&',
    '&lt;': '<',
    '#60;': '<',
    '&gt;': '>',
    '#62;': '>',
    '&quot;': '"',
    '#34;': '"',
    '&apos;': "'",
    '#39;': "'",
    '&#8217;': '’',
    '&#8216;': '‘',
    '&#8211;': '–',
    '&#8212;': '—',
  }

  const pattern = new RegExp(Object.keys(entities).join('|'), 'g')
  return input.replace(pattern, (match) => entities[match])
}
